import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { WhatsappLogo, QrCode, Users, Gear, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const STATUS_BADGE = {
  official: { label: "🟢 Official group", cls: "border-green-200 bg-green-50 text-green-800" },
  community: { label: "🟡 Community group", cls: "border-amber-200 bg-amber-50 text-amber-800" },
  unverified: { label: "⚠️ Unverified", cls: "border-slate-200 bg-slate-50 text-slate-600" },
};

export default function WhatsAppGroupBox({ groupId, groupName, canManageHint }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [form, setForm] = useState({ url: "", status: "community" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/public/groups/${groupId}/whatsapp`).then((r) => setData(r.data)).catch(() => setData({ connected: false, interest_count: 0 }));
  useEffect(() => { load(); }, [groupId]);

  const connect = async () => {
    setBusy(true);
    try { await api.post(`/groups/${groupId}/whatsapp`, form); toast.success("WhatsApp group connected"); setConnectOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => { try { await api.delete(`/groups/${groupId}/whatsapp`); toast.success("Disconnected"); load(); } catch { toast.error("Not allowed"); } };
  const toggleInterest = async () => {
    if (!user) { toast.info("Sign in to show interest"); nav("/login"); return; }
    try { const { data: r } = await api.post(`/groups/${groupId}/whatsapp/interest`); toast.success(r.is_interested ? "Marked interested" : "Removed"); load(); }
    catch { toast.error("Could not update"); }
  };

  if (!data) return null;
  const qr = data.url ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(data.url)}` : "";
  const badge = STATUS_BADGE[data.status] || STATUS_BADGE.community;

  return (
    <div className="mt-4 rounded-2xl border border-green-200 bg-green-50/60 p-4" data-testid="whatsapp-box">
      <div className="flex items-center gap-2">
        <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
        <h3 className="font-heading text-base font-medium">WhatsApp alumni group</h3>
        {data.connected && <Badge variant="outline" className={`rounded-full text-xs font-normal ${badge.cls}`}>{badge.label}</Badge>}
      </div>

      {data.connected ? (
        <>
          <p className="mt-1 text-sm text-muted-foreground">Chat in real time, get reunion updates and stay in touch with classmates.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full gap-1.5 bg-[#25D366] text-white hover:bg-[#1eb457]" data-testid="whatsapp-join-btn"><WhatsappLogo size={16} weight="fill" /> Join WhatsApp Group</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-sm" data-testid="whatsapp-join-dialog">
                <DialogHeader>
                  <DialogTitle>Join {groupName} on WhatsApp</DialogTitle>
                  <DialogDescription>By joining, you'll share your WhatsApp identity (name & number) with the group's members, per WhatsApp's privacy rules. AlumniPulse doesn't control WhatsApp settings.</DialogDescription>
                </DialogHeader>
                {qr && <img src={qr} alt="WhatsApp QR" className="mx-auto rounded-lg border border-border/60 bg-white p-2" data-testid="whatsapp-qr" />}
                <label className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} className="mt-0.5" data-testid="whatsapp-ack" /> I understand and want to join
                </label>
                <DialogFooter>
                  <a href={ack ? data.url : undefined} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!ack) { e.preventDefault(); toast.info("Please acknowledge first"); } }} className="w-full">
                    <Button disabled={!ack} className="w-full rounded-full gap-1.5 bg-[#25D366] text-white hover:bg-[#1eb457]" data-testid="whatsapp-open-btn"><WhatsappLogo size={16} weight="fill" /> Open WhatsApp</Button>
                  </a>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <a href={qr} download target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-full gap-1.5"><QrCode size={15} /> QR code</Button></a>
            {user && (
              <Button size="sm" variant="ghost" className="rounded-full gap-1.5 text-muted-foreground" onClick={disconnect} data-testid="whatsapp-disconnect"><X size={14} /> Disconnect</Button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="mt-1 text-sm text-muted-foreground">No WhatsApp group yet. Show interest so an admin creates one — or connect an existing group's invite link.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant={data.is_interested ? "secondary" : "outline"} className="rounded-full gap-1.5" onClick={toggleInterest} data-testid="whatsapp-interest-btn">
              <Users size={15} /> {data.is_interested ? "Interested ✓" : "I'm interested"}{data.interest_count > 0 ? ` (${data.interest_count})` : ""}
            </Button>
            <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="ghost" className="rounded-full gap-1.5 text-green-700" data-testid="whatsapp-connect-open"><Gear size={15} /> Connect a WhatsApp group</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md" data-testid="whatsapp-connect-dialog">
                <DialogHeader>
                  <DialogTitle>Connect a WhatsApp group</DialogTitle>
                  <DialogDescription>Create the group in WhatsApp, copy its invite link (Group → Invite via link), and paste it here. AlumniPulse never creates or controls WhatsApp groups.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div><Label>WhatsApp invite link</Label><Input className="mt-1" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://chat.whatsapp.com/…" data-testid="whatsapp-url-input" /></div>
                  <div>
                    <Label>Group type</Label>
                    <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger className="mt-1" data-testid="whatsapp-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="community">Community group</SelectItem>
                        <SelectItem value="official">Official group (admin only)</SelectItem>
                        <SelectItem value="unverified">Unverified</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={connect} disabled={busy} className="rounded-full gap-1.5 bg-[#25D366] text-white hover:bg-[#1eb457]" data-testid="whatsapp-connect-submit"><WhatsappLogo size={15} weight="fill" /> {busy ? "Connecting…" : "Verify & Connect"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </>
      )}
    </div>
  );
}
